/**
 * @mixin DialogMixin
 * @description A mixin that provides dialog functionality with input/output capabilities.
 *
 * @property {Object} data
 * @property {boolean} data.isVisible - Determines whether the dialog is currently visible
 * @property {Object} data.dialogData - Stores input/output data for the dialog
 *
 * @method show - Makes the dialog visible with optional input data
 * @method showDialog - Returns a Promise that resolves with output data when dialog is confirmed
 * @method hide - Hides the dialog
 * @method emitOk - Emits the 'ok' event with output data and hides the dialog
 * @method emitCancel - Emits the 'cancel' event and hides the dialog
 *
 * @emits ok - Emitted with output data when OK action is triggered
 * @emits cancel - Emitted when Cancel action is triggered
 * @emits dialog-close - Emitted when dialog is closed
 *
 * @example this.$refs.myDialog.show({ initialValue: 'some data' })
 * @example const result = await this.$refs.myDialog.showDialog({ initialValue: 'some data' })
 */
export default {
  props:{

  },
  data() {
    return {
      isVisible: false,
      dialogData: null,
      outputData: null,
      dialogResolve: null // Store the promise resolve function
    };
  },
  methods: {
    // Method to show the dialog with optional input data
    show(inputData = null) {
      this.dialogData = inputData || {};
      this.isVisible = true;
      this.outputData = {};
    },
    // Method to show dialog and return a Promise with output data
    showDialog(inputData = null, outputData = null) {
      return new Promise((resolve, reject) => {
        this.dialogData = inputData || {};
        this.isVisible = true;
        this.outputData = outputData || {};
        this.dialogResolve = resolve;
      });
    },
    // Method to hide the dialog
    hide() {
      this.isVisible = false;
      this.$emit('dialog-close');
      // If the dialog is hidden (e.g. via overlay click) and promise is pending, resolve with null
      if (this.dialogResolve) {
        this.dialogResolve(null);
        this.dialogResolve = null;
      }
    },
    // Emits the 'ok' event with output data and hides the dialog
    emitOk() {
      this.$emit('ok', this.outputData);

      // Resolve the promise if it exists
      if (this.dialogResolve) {
        this.dialogResolve(this.outputData);
        this.dialogResolve = null;
      }
      this.hide();
    },
    // Emits the 'cancel' event and hides the dialog
    emitCancel() {
      this.$emit('cancel');

      // Resolve the promise with null if it exists
      if (this.dialogResolve) {
        this.dialogResolve(null);
        this.dialogResolve = null;
      }
      this.hide();
    }
  }
}
